import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileDetail, Endpoint } from './useFileUpload'
import { useFetchWithAuth } from '../session/useFetchWithAuth'

const URL = import.meta.env.VITE_EPC_API_URL ?? ''

/**
 * Hook for getting temporary presigned URLs for file uploads
 */
export const useTempPresignedUrls = () => {
  const fetchWithAuth = useFetchWithAuth()

  return useMutation({
    mutationFn: async ({
      files,
    }: {
      files: FileDetail[]
    }): Promise<Endpoint[]> => {
      try {
        const data = await fetchWithAuth(`${URL}/temp-file-uploads`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ files }),
        })

        return data
      } catch (error) {
        console.error('Failed to get temporary presigned URLs:', error)
        throw new Error('Failed to get temporary presigned URLs')
      }
    },
  })
}

/**
 * Interface for a temporary file
 */
export interface TempFile {
  id: string
  name: string
  contentType: string
  url: string
  size?: number
  createdAt: string
  expiresAt: string
}

/**
 * Hook for fetching all temporary files uploaded by the current user
 */
export const useTempFiles = () => {
  const fetchWithAuth = useFetchWithAuth()

  return useQuery({
    queryKey: ['tempFiles'],
    queryFn: async (): Promise<TempFile[]> => {
      try {
        const data = await fetchWithAuth(`${URL}/temp-file-uploads`)
        return data.files || []
      } catch (error) {
        console.error('Failed to fetch temporary files:', error)
        throw new Error('Failed to fetch temporary files')
      }
    },
  })
}

/**
 * Hook for deleting a temporary file
 */
export const useDeleteTempFile = () => {
  const queryClient = useQueryClient()
  const fetchWithAuth = useFetchWithAuth()

  return useMutation({
    mutationFn: async (fileId: string): Promise<void> => {
      try {
        await fetchWithAuth(`${URL}/temp-file-uploads/${fileId}`, {
          method: 'DELETE',
        })
      } catch (error) {
        console.error('Failed to delete temporary file:', error)
        throw new Error('Failed to delete temporary file')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tempFiles'] })
    },
  })
}

/**
 * Hook for creating an event with temporary files
 */
export const useCreateEventWithFiles = () => {
  const queryClient = useQueryClient()
  const fetchWithAuth = useFetchWithAuth()

  return useMutation({
    mutationFn: async ({
      eventData,
      tempFileIds,
    }: {
      eventData: any
      tempFileIds: string[]
    }) => {
      try {
        const data = await fetchWithAuth(`${URL}/events/create-with-files`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            event_data: eventData,
            temp_file_ids: tempFileIds,
          }),
        })

        return data
      } catch (error) {
        console.error('Failed to create event with temporary files:', error)
        throw new Error('Failed to create event with temporary files')
      }
    },
    onSuccess: () => {
      // Invalidate both events and temp files queries
      queryClient.invalidateQueries({ queryKey: ['events'] })
      queryClient.invalidateQueries({ queryKey: ['tempFiles'] })
    },
  })
}
